import React, { useState } from 'react';
import { Modal, Form, Input, Button, message } from 'antd';
import { LockOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { updatePassword } from '@/services/ant-design-pro/api';
import JSEncrypt from 'jsencrypt';
import { getRSAPublicKey } from '@/services/ant-design-pro/login';

interface ChangePasswordModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: any) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('两次输入的密码不一致');
      return;
    }

    setLoading(true);
    try {
      // 获取RSA公钥（优先使用缓存的公钥）
      let publicKey = localStorage.getItem('publicKey');
      if (!publicKey) {
        const rsaResponse = await getRSAPublicKey();
        if (!rsaResponse?.success) {
          throw new Error('获取RSA公钥失败');
        }
        publicKey = rsaResponse?.data?.publicKey || '';
        if (!publicKey) {
          throw new Error('RSA公钥为空');
        }
        // 缓存公钥到localStorage
        localStorage.setItem('publicKey', publicKey);
      }

      // 使用RSA公钥加密密码
      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(publicKey);
      const encryptedOldPassword = encrypt.encrypt(values.oldPassword || '');
      const encryptedNewPassword = encrypt.encrypt(values.newPassword || '');

      // 调用修改密码接口
      await updatePassword({
        oldPassword: encryptedOldPassword || '',
        password: encryptedNewPassword || '',
      });

      message.success('密码修改成功');
      form.resetFields();
      onSuccess();
    } catch (error) {
      console.error('修改密码失败:', error);
      // message.error('修改密码失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="修改密码"
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          取消
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          loading={loading}
          onClick={() => form.submit()}
        >
          提交
        </Button>
      ]}
      width={500}
    >
      <div style={{ marginBottom: '20px', color: '#666' }}>
        请输入原密码并设置符合安全规则的新密码
      </div>
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
      >
        <Form.Item
          name="oldPassword"
          label="原密码"
          rules={[
            {
              required: true,
              message: '请输入原密码！',
            },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="请输入原密码"
            size="large"
            iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label="新密码"
          rules={[
            {
              required: true,
              message: '请输入新密码！',
            },
            {
              min: 8,
              max: 16,
              message: '密码长度必须为8~16位！',
            },
            {
              pattern: /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).+$/,
              message: '密码必须包含英文大写、英文小写和数字！',
            },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="请输入新密码"
            size="large"
            iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="确认密码"
          rules={[
            {
              required: true,
              message: '请确认新密码！',
            },
            {
              min: 8,
              max: 16,
              message: '密码长度必须为8~16位！',
            },
            {
              pattern: /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).+$/,
              message: '密码必须包含英文大写、英文小写和数字！',
            },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="请再次输入新密码"
            size="large"
            iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal;